import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "./locales/en.json";
import fr from "./locales/fr.json";
import es from "./locales/es.json";
import it from "./locales/it.json";

/**
 * i18n setup for the app UI.
 *
 * Four bundled locales (English, French, Spanish, Italian), shipped
 * inline so translations are available synchronously — no async
 * backend, so `t()` returns real strings on first render (important
 * for tests and SSR-free hydration).
 *
 * Language selection order: an explicit choice persisted in
 * `localStorage` (under `thebeat.lang`) wins; otherwise we fall back to
 * the browser's `navigator.language`, then to English. The
 * `<LanguageSwitcher>` writes the chosen language back to
 * `localStorage` via `i18n.changeLanguage`.
 */

export const SUPPORTED_LANGUAGES = ["en", "fr", "es", "it"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/** Human-readable names, shown in the language switcher. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  en: "English",
  fr: "Français",
  es: "Español",
  it: "Italiano",
};

/** BCP-47 locale used for date / number formatting per app language. */
export const LOCALE_TAGS: Record<Language, string> = {
  en: "en-US",
  fr: "fr-FR",
  es: "es-ES",
  it: "it-IT",
};

export const STORAGE_KEY = "thebeat.lang";

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      es: { translation: es },
      it: { translation: it },
    },
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    fallbackLng: "en",
    // `navigator.language` gives e.g. "fr-CH"; match on the base tag.
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
    interpolation: {
      // React already escapes rendered values.
      escapeValue: false,
    },
    returnNull: false,
  });

/** The current app language, narrowed to a supported tag. */
export function currentLanguage(): Language {
  const base = i18n.resolvedLanguage ?? i18n.language ?? "en";
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base)
    ? (base as Language)
    : "en";
}

/** BCP-47 locale tag for the current app language. */
export function currentLocale(): string {
  return LOCALE_TAGS[currentLanguage()];
}

/**
 * Mirror the active app language onto `<html lang>`.
 *
 * Mobile browsers (and assistive tech, `:lang()` CSS, hyphenation, font
 * fallback) trust the document language. Leaving it hardcoded to `en`
 * while the visible copy is French/Spanish/Italian makes Chrome offer a
 * "Translate this page?" prompt even when the app is already in the
 * user's language.
 */
function syncDocumentLang(lng: string) {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
}

// Initial language (detected or persisted) may not be English, so sync it
// once now, then on every subsequent `changeLanguage`.
syncDocumentLang(currentLanguage());
i18n.on("languageChanged", syncDocumentLang);

export default i18n;
